// A collapsible category inside the menu panel: a tinted header with its name and item count, then its rows, its
// sub-categories under sub-headers, and a footer (the add button). A hidden category reads dimmed with a "Nascosto"
// badge, so it can still be found and switched back (STR-06). A header's actions sit beside its toggle: the ⋮, or
// Riordina's handle and button.
import type { ReactNode } from "react";
import { ChevronDownIcon } from "@/components/icons";
import { cn } from "@/utils/cn";
import type { RowGroup } from "../layout";
import type { MenuCategory, MenuItem } from "../types";
import { HiddenBadge } from "./ItemRow";

interface Props {
  category: MenuCategory;
  count: number;
  open: boolean;
  onToggle: () => void;
  /** Header-right actions: the category's ⋮, or its handle and Riordina in reorder mode. */
  actions?: ReactNode;
  /** The rename input, shown in place of the toggle while the name is edited. */
  editing?: ReactNode;
  /** Another category is being reordered: this one steps back. */
  dimmed?: boolean;
  children: ReactNode;
  footer?: ReactNode;
}

export function CategorySection({ category, count, open, onToggle, actions, editing, dimmed = false, children, footer }: Props) {
  return (
    <section className={cn("transition-opacity", dimmed && "pointer-events-none opacity-40")} aria-label={category.name}>
      <div className="flex items-center gap-2 rounded-xl bg-gray-50 pr-3">
        {editing ? (
          <div className="flex min-w-0 flex-1 py-1.5 pl-2">{editing}</div>
        ) : (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            aria-label={category.name}
            className="flex min-w-0 flex-1 items-center gap-2 py-2.5 pl-3 text-left"
          >
            <ChevronDownIcon className={cn("h-4 w-4 shrink-0 text-gray-400 transition-transform", !open && "-rotate-90")} />
            <span className={cn("min-w-0 text-[13px] font-semibold leading-[18px] [overflow-wrap:anywhere]", category.hidden ? "text-gray-500" : "text-gray-900")}>
              {category.name}
            </span>
            {category.hidden && <HiddenBadge />}
            <span className="ml-auto shrink-0 pl-2 text-[12px] tabular-nums text-gray-400">{count}</span>
          </button>
        )}
        {actions}
      </div>
      {open && (
        <div className="px-3 pb-2 pt-1">
          {children}
          {footer}
        </div>
      )}
    </section>
  );
}

/**
 * The rows of a category, each sub-category under its sub-header, which can carry the sub-category's ⋮ (`groupAside`)
 * and its rename input in place of its name (`groupEditing`).
 */
export function GroupedRows({ groups, renderItem, groupAside, groupEditing }: {
  groups: RowGroup[];
  renderItem: (item: MenuItem) => ReactNode;
  groupAside?: (group: RowGroup) => ReactNode;
  groupEditing?: (group: RowGroup) => ReactNode;
}) {
  const total = groups.reduce((n, g) => n + g.items.length, 0);
  // A sub-category with no items yet still shows its header, so its ⋮ can be reached.
  if (total === 0 && groups.every((g) => g.label === null)) {
    return <p className="py-3 text-[13px] text-gray-400">Nessuna voce in questa categoria.</p>;
  }
  return (
    <>
      {groups.map((group) => (
        <div key={group.key}>
          {group.label !== null && (
            <div className="flex items-center gap-1.5 pt-2">
              {groupEditing?.(group) ?? (
                <p className={cn("flex min-w-0 flex-1 items-center gap-1.5 py-1 text-[12px] font-medium text-gray-500", group.hidden && "opacity-60")}>
                  {group.label}
                  {group.hidden && <HiddenBadge />}
                </p>
              )}
              {groupAside?.(group)}
            </div>
          )}
          <div className={cn("divide-y divide-gray-100", group.hidden && "opacity-60")}>
            {group.items.map((item) => (
              <div key={item.id}>{renderItem(item)}</div>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}
